import type {
  ApiErrorResponse,
  CheckRequest,
  CheckResponse,
} from '@domaintriage/shared';

const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

/**
 * Shape thrown on API failures so callers can inspect `code` and `message`
 * without a `try { JSON.parse(...) }` dance.
 */
export class ApiError extends Error {
  /** Machine-readable error code from the backend. */
  code: string;
  /** HTTP status code (0 when the request never reached the server). */
  status: number;
  /** Optional per-domain error details from the backend. */
  details?: ApiErrorResponse['error']['details'];

  constructor(
    message: string,
    code: string,
    status: number,
    details?: ApiErrorResponse['error']['details'],
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Call `POST /api/v1/check` and return the parsed response.
 *
 * @param request - Domains (and optional TLDs) to check.
 * @param signal  - Optional `AbortSignal` for cancellation.
 * @returns Typed `CheckResponse` from the backend.
 * @throws {ApiError} on HTTP errors, malformed responses, or network failures.
 */
export async function checkDomains(
  request: CheckRequest,
  signal?: AbortSignal,
): Promise<CheckResponse> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/api/v1/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    });
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw err; // Let callers distinguish cancellation from real errors.
    }
    throw new ApiError(
      'Network error — please check your connection and try again.',
      'NETWORK_ERROR',
      0,
    );
  }

  // Attempt to parse the body regardless of status — the backend returns
  // structured JSON errors too.
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError(
      `Unexpected response from the server (HTTP ${response.status}).`,
      'PARSE_ERROR',
      response.status,
    );
  }

  if (!response.ok) {
    const errorBody = body as ApiErrorResponse | undefined;
    const apiErr = errorBody?.error;
    throw new ApiError(
      apiErr?.message ?? `Request failed (HTTP ${response.status}).`,
      apiErr?.code ?? 'UNKNOWN_ERROR',
      response.status,
      apiErr?.details,
    );
  }

  return body as CheckResponse;
}
